import { inject, Injectable } from '@angular/core';
import {
  ApiDependencyProviderService,
  CollectionRequest,
  CollectionResponse,
  HttpParams,
} from '@web/common';
import { blank, Guid } from '@web/utils';
import { map, Observable } from 'rxjs';
import { AdminBoxQuestionnaireCollectionItemResponse } from '../responses/admin-box-questionnaire-collection-item.response';
import { AdminBoxQuestionnaireDetailsResponse } from '../responses/admin-box-questionnaire-details.response';
import { AdminBoxQuestionnaireChangeSelectionRequest } from '../requests/admin-box-questionnaire-change-selection.request';

@Injectable({
  providedIn: 'root',
})
export class AdminBoxQuestionnaireApiService {
  private readonly provider = inject(ApiDependencyProviderService);

  public fetchCollection(
    request: CollectionRequest,
  ): Observable<
    CollectionResponse<AdminBoxQuestionnaireCollectionItemResponse>
  > {
    const path = 'admin/box-questionnaire';
    const url = this.provider.httpUrlFactory.withQueryParams(
      this.hostUrl(),
      path,
      this.provider.httpHelper.collectionRequestToQueryParams(request),
    );
    const context = this.provider.httpContextBuilder
      .skipByHttpErrorInterceptor()
      .build();

    return this.provider.http
      .get(url, { context })
      .pipe(
        map(
          this.provider.httpHelper.bodyToCollectionResponse(
            AdminBoxQuestionnaireCollectionItemResponse,
          ),
        ),
      );
  }

  public fetchDetails(
    id: Guid,
  ): Observable<AdminBoxQuestionnaireDetailsResponse> {
    const path = `admin/box-questionnaire/${HttpParams.Id}`;
    const url = this.provider.httpUrlFactory.withParams(this.hostUrl(), path, {
      [HttpParams.Id]: id,
    });
    const context = this.provider.httpContextBuilder
      .skipByLoadingInterceptor()
      .build();

    return this.provider.http
      .get(url, { context })
      .pipe(
        map(
          this.provider.httpHelper.bodyToResponse(
            AdminBoxQuestionnaireDetailsResponse,
          ),
        ),
      );
  }

  public resumeById(id: Guid): Observable<void> {
    const path = `admin/box-questionnaire/${HttpParams.Id}/resume`;
    const url = this.provider.httpUrlFactory.withParams(this.hostUrl(), path, {
      [HttpParams.Id]: id,
    });

    return this.provider.http.post(url, {}).pipe(blank());
  }

  public endById(id: Guid): Observable<void> {
    const path = `admin/box-questionnaire/${HttpParams.Id}/end`;
    const url = this.provider.httpUrlFactory.withParams(this.hostUrl(), path, {
      [HttpParams.Id]: id,
    });

    return this.provider.http.post(url, {}).pipe(blank());
  }

  public changeSelection(questionnaireId: Guid, request: AdminBoxQuestionnaireChangeSelectionRequest): Observable<void> {
    const path = `admin/box-questionnaire/${HttpParams.Id}/change-selection`;
    const url = this.provider.httpUrlFactory.withParams(this.hostUrl(), path, {
      [HttpParams.Id]: questionnaireId,
    });

    const body = this.provider.httpHelper.requestToBody(
      request,
      AdminBoxQuestionnaireChangeSelectionRequest,
    );
    const context = this.provider.httpContextBuilder
      .skipByHttpErrorInterceptor()
      .build();

    return this.provider.http.post(url, body, { context }).pipe(blank());
  }

  private hostUrl(): string {
    return this.provider.environment.ApiHostUrl.Shop;
  }
}
